/**
 * Draft-preview page (PF-335): renders the server-generated borrador PDF
 * (`GET …/sales-invoices/:invoiceId/preview-pdf`) inside a full-height
 * iframe. The blob URL is same-origin, so the toolbar "Imprimir" drives the
 * iframe's own print dialog; "Descargar" saves the same blob. The BFF answers
 * 409 PREVIEW_NOT_AVAILABLE for emitted / in-flight invoices — surfaced via
 * the shared <ErrorState /> (HTTP status + code + Spanish message).
 */
import { useEffect, useMemo, useRef } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Download, Loader2, Printer } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { Button } from "@/components/ui/button";
import {
  usePreviewInvoicePdf,
  useSalesInvoice,
} from "@/lib/api/sales-invoices.queries";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute("/companies/$companyId/facturas/$invoiceId/preview")({
  component: InvoicePreviewPage,
});

function InvoicePreviewPage() {
  return (
    <RequireAuth>
      <PreviewContent />
    </RequireAuth>
  );
}

function PreviewContent() {
  const { t } = useTranslation();
  const { companyId, invoiceId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const invoiceQuery = useSalesInvoice(companyId, invoiceId);
  const pdfQuery = usePreviewInvoicePdf(companyId, invoiceId);

  const pdfBlob = pdfQuery.data;
  // Blob → object URL, revoked when the blob changes and on unmount.
  const objectUrl = useMemo(
    () => (pdfBlob ? URL.createObjectURL(pdfBlob) : null),
    [pdfBlob],
  );
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const documentId = invoiceQuery.data?.documentId ?? invoiceId;
  const fileName = `borrador-${documentId}.pdf`;

  function onPrint() {
    // Blob URLs are same-origin — the iframe window is scriptable.
    const frameWindow = iframeRef.current?.contentWindow;
    if (!frameWindow) return;
    frameWindow.focus();
    frameWindow.print();
  }

  function onDownload() {
    if (!objectUrl) return;
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }

  return (
    <div className="flex h-screen flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Button asChild variant="outline" size="sm">
            <Link
              to="/companies/$companyId/facturas/$invoiceId"
              params={{ companyId, invoiceId }}
            >
              <ArrowLeft className="h-4 w-4" />
              {t("invoices.preview.back")}
            </Link>
          </Button>
          <div>
            <h1 className="text-sm font-semibold">{t("invoices.preview.title")}</h1>
            <p className="font-mono text-xs text-muted-foreground">{documentId}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onDownload} disabled={!objectUrl}>
            <Download className="h-4 w-4" />
            {t("invoices.preview.download")}
          </Button>
          <Button size="sm" onClick={onPrint} disabled={!objectUrl}>
            <Printer className="h-4 w-4" />
            {t("invoices.preview.print")}
          </Button>
        </div>
      </div>

      {pdfQuery.isPending ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t("invoices.preview.loading")}</p>
        </div>
      ) : pdfQuery.isError || !objectUrl ? (
        <div className="flex flex-1 items-center justify-center">
          <ErrorState
            title={t("invoices.preview.error_title")}
            description={t("invoices.preview.error_body")}
            error={pdfQuery.error}
            onRetry={() => void pdfQuery.refetch()}
            retryLabel={t("common.retry")}
          />
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          src={objectUrl}
          title={t("invoices.preview.title")}
          className="w-full flex-1 rounded-lg border bg-muted"
        />
      )}
    </div>
  );
}
